import React, { useState, useEffect, useRef } from 'react';
import { customerService } from '../../services/customerService';
import CustomerModal from '../CustomerModal';
import CustomerHistoryModal from '../CustomerHistoryModal';

const CustomerSelector = ({
  selectedCustomer,
  onSelect,
  onClear,
  showHistory = true
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [retryTick, setRetryTick] = useState(0);
  const [showDropdown, setShowDropdown] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);

  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  // Zamknij dropdown przy kliknięciu poza komponentem
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // [M-cust] Wyszukiwanie po stronie serwera (endpoint /customers/search) z debounce.
  // Dzięki temu widzimy wszystkich pasujących klientów, a nie tylko pierwszych 100
  // pobranych domyślnym limitem backendu.
  useEffect(() => {
    const term = searchTerm.trim();
    if (term.length < 2) {
      setFilteredCustomers([]);
      setSearchError(null);
      setShowDropdown(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setSearchError(null);

    const timer = setTimeout(async () => {
      try {
        // Wysylka dotyczy odbiorcow - dostawcy z tej samej kartoteki sa odfiltrowani
        const res = await customerService.searchCustomers(term, 20, 'odbiorca');
        if (cancelled) return;
        // Backend zwraca { success, data: { customers: [...] } }
        const results = res?.data?.customers || res?.customers || [];
        setFilteredCustomers(Array.isArray(results) ? results : []);
        setShowDropdown(true);
      } catch (error) {
        if (cancelled) return;
        console.error('Błąd wyszukiwania klientów:', error);
        // [L-custerr] Odróżnij błąd sieci/serwera od pustego wyniku
        setSearchError(error?.message || 'Nie udało się pobrać listy klientów');
        setFilteredCustomers([]);
        setShowDropdown(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [searchTerm, retryTick]);

  const handleSelectCustomer = async (customer) => {
    setShowDropdown(false);
    setSearchTerm('');
    // [M-cust] Endpoint /customers/search zwraca OKROJONY obiekt (name/phone/
    // address zamiast imie/nazwisko/telefon/ulica/miasto/kod_pocztowy/wysylka_*).
    // Reszta modulu (budowa adresu przesylki, payload kuriera) polega na pelnych
    // polach — docagamy komplet po id zanim przekazemy wybor wyzej.
    try {
      const full = await customerService.getCustomer(customer.id);
      onSelect(full || customer);
    } catch (error) {
      console.error('Błąd pobierania pełnych danych klienta:', error);
      // Fallback: przekaz co mamy, ale zasygnalizuj brak kompletu danych
      setSearchError(error?.message || 'Nie udało się pobrać pełnych danych klienta');
      onSelect(customer);
    }
  };

  const handleClearCustomer = () => {
    onClear();
    setSearchTerm('');
    inputRef.current?.focus();
  };

  const handleNewCustomerSuccess = (newCustomer) => {
    // Wybierz nowo utworzonego klienta (lista jest wyszukiwana po stronie serwera)
    onSelect(newCustomer);
    setShowAddModal(false);
  };

  const handleEditCustomerSuccess = (updatedCustomer) => {
    // Zaktualizuj wybranego klienta
    onSelect(updatedCustomer);
    setShowEditModal(false);
  };

  const getCustomerDisplayName = (customer) => {
    if (customer.nazwa_firmy) {
      return customer.nazwa_firmy;
    }
    return `${customer.imie || ''} ${customer.nazwisko || ''}`.trim() || 'Bez nazwy';
  };

  // Style
  const inputStyle = {
    width: '100%',
    padding: '6px 10px',
    fontSize: '12px',
    border: '1px solid var(--border)',
    borderRadius: '6px',
    backgroundColor: 'var(--card-bg)',
    color: 'var(--text)',
    outline: 'none'
  };

  const dropdownStyle = {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    backgroundColor: 'var(--card-bg)',
    border: '1px solid var(--border)',
    borderRadius: '0 0 4px 4px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
    maxHeight: '300px',
    overflowY: 'auto',
    zIndex: 1000
  };

  const itemStyle = {
    padding: '0.5rem 0.75rem',
    cursor: 'pointer',
    borderBottom: '1px solid var(--bg)',
    fontSize: '12px'
  };

  const buttonStyle = {
    padding: '6px 12px',
    fontSize: '12px',
    fontWeight: '500',
    display: 'flex',
    alignItems: 'center',
    gap: '0.35rem'
  };

  // Jeśli wybrany klient - pokaż jego dane
  if (selectedCustomer) {
    return (
      <div style={{
        backgroundColor: 'var(--info-light)',
        border: '1px solid var(--info-light)',
        borderRadius: '8px',
        padding: '0.75rem'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <div style={{ fontWeight: '600', fontSize: '13px', marginBottom: '4px' }}>
              {selectedCustomer.nazwa_firmy ? '🏢' : '👤'} {getCustomerDisplayName(selectedCustomer)}
            </div>
            {selectedCustomer.nazwa_firmy && selectedCustomer.imie && (
              <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
                Osoba kontaktowa: {selectedCustomer.imie} {selectedCustomer.nazwisko}
              </div>
            )}
            {selectedCustomer.email && (
              <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                📧 {selectedCustomer.email}
              </div>
            )}
            {selectedCustomer.telefon && (
              <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                📱 {selectedCustomer.telefon}
              </div>
            )}
            {(selectedCustomer.adres || selectedCustomer.ulica) && (
              <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '4px' }}>
                📍 {selectedCustomer.ulica || selectedCustomer.adres}, {selectedCustomer.kod_pocztowy} {selectedCustomer.miasto}
              </div>
            )}
            {selectedCustomer.wysylka_inna && (
              <div style={{
                fontSize: '10px',
                color: 'var(--info)',
                marginTop: '4px',
                padding: '2px 6px',
                backgroundColor: 'var(--info-light)',
                borderRadius: '3px',
                display: 'inline-block'
              }}>
                📦 Inny adres wysyłki: {selectedCustomer.wysylka_ulica}, {selectedCustomer.wysylka_kod_pocztowy} {selectedCustomer.wysylka_miasto}
              </div>
            )}
          </div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button
              onClick={() => setShowEditModal(true)}
              style={{ ...buttonStyle, border: '1px solid var(--warning)', color: 'var(--warning)' }}
              title="Edytuj dane klienta"
            >
              ✏️ Edytuj
            </button>
            {showHistory && (
              <button
                onClick={() => setShowHistoryModal(true)}
                style={{ ...buttonStyle, border: '1px solid var(--info)', color: 'var(--info)' }}
                title="Historia klienta"
              >
                📋 Historia
              </button>
            )}
            <button
              onClick={handleClearCustomer}
              style={{ ...buttonStyle, border: '1px solid var(--text-muted)', color: 'var(--text-muted)' }}
            >
              🔄 Zmień
            </button>
          </div>
        </div>

        {/* Modal edycji klienta */}
        <CustomerModal
          isOpen={showEditModal}
          initialData={selectedCustomer}
          onSuccess={handleEditCustomerSuccess}
          onClose={() => setShowEditModal(false)}
        />

        {/* Modal historii */}
        <CustomerHistoryModal
          customerId={selectedCustomer?.id}
          customerName={getCustomerDisplayName(selectedCustomer)}
          isOpen={showHistoryModal}
          onClose={() => setShowHistoryModal(false)}
        />
      </div>
    );
  }

  // Wyszukiwarka klienta
  return (
    <div ref={wrapperRef} style={{ position: 'relative' }}>
      <div style={{ display: 'flex', gap: '0.5rem' }}>
        <div style={{ flex: 1, position: 'relative' }}>
          <input
            ref={inputRef}
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onFocus={() => searchTerm.length >= 2 && setShowDropdown(true)}
            placeholder="Wyszukaj klienta (imię, nazwisko, email, telefon, firma)..."
            style={inputStyle}
          />
          {loading && (
            <span style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)' }}>
              ⏳
            </span>
          )}
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          style={{ ...buttonStyle, border: '1px solid var(--success)', color: 'var(--success)' }}
        >
          ➕ Nowy klient
        </button>
      </div>

      {/* Dropdown z wynikami */}
      {showDropdown && filteredCustomers.length > 0 && (
        <div style={dropdownStyle}>
          {filteredCustomers.map((customer) => (
            <div
              key={customer.id}
              style={itemStyle}
              onClick={() => handleSelectCustomer(customer)}
              onMouseEnter={(e) => e.target.style.backgroundColor = 'var(--bg)'}
              onMouseLeave={(e) => e.target.style.backgroundColor = 'var(--card-bg)'}
            >
              <div style={{ fontWeight: '500' }}>
                {customer.nazwa_firmy ? '🏢' : '👤'} {getCustomerDisplayName(customer)}
              </div>
              <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '2px' }}>
                {customer.email && <span style={{ marginRight: '10px' }}>📧 {customer.email}</span>}
                {customer.telefon && <span>📱 {customer.telefon}</span>}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* [L-custerr] Błąd pobrania listy klientów (sieć/serwer) - odróżniony od pustego wyniku */}
      {showDropdown && searchError && !loading && (
        <div style={{ ...dropdownStyle, padding: '1rem', textAlign: 'center', color: 'var(--danger)' }}>
          <div>⚠️ {searchError}</div>
          <button
            onClick={() => setRetryTick((t) => t + 1)}
            style={{ ...buttonStyle, border: '1px solid var(--info)', color: 'var(--info)', marginTop: '0.5rem', margin: '0.5rem auto 0' }}
          >
            🔄 Spróbuj ponownie
          </button>
        </div>
      )}

      {/* Brak wyników (dopiero gdy nie ma błędu) */}
      {showDropdown && searchTerm.trim().length >= 2 && filteredCustomers.length === 0 && !loading && !searchError && (
        <div style={{ ...dropdownStyle, padding: '1rem', textAlign: 'center', color: 'var(--text-muted)' }}>
          <div>Nie znaleziono klienta</div>
          <button
            onClick={() => { setShowDropdown(false); setShowAddModal(true); }}
            style={{ ...buttonStyle, border: '1px solid var(--success)', color: 'var(--success)', marginTop: '0.5rem', margin: '0.5rem auto 0' }}
          >
            ➕ Dodaj nowego klienta
          </button>
        </div>
      )}

      {/* Modal dodawania klienta */}
      <CustomerModal
        isOpen={showAddModal}
        onSuccess={handleNewCustomerSuccess}
        onClose={() => setShowAddModal(false)}
      />
    </div>
  );
};

export default CustomerSelector;
